import { formatGridSize, type GridStepInfo } from "../../lib/gridStep";

interface Props {
  step: GridStepInfo;
  unit: "imperial" | "metric";
}

/** Small bottom-left pill that reports the active grid spacing. Designed to
 *  visually rhyme with the bottom-right "Recenter" affordance: same height,
 *  same rounded shape, same shadow/ring treatment, same safe-area offset on
 *  iOS. The label tracks the `coarse` step (the dominant lines the user sees
 *  at any moment) and a tiny preview tile shows two intersecting grid lines
 *  at the same density so the legend is glanceable, not just textual. */
export function GridLegend({ step, unit }: Props) {
  const label = formatGridSize(step.coarse, unit);
  return (
    <div
      className="absolute left-3 bottom-[var(--canvas-overlay-bottom)] z-10 inline-flex items-center gap-1.5 h-9 px-3 rounded-full bg-white ring-1 ring-ink-200 text-xs font-medium text-ink-700 shadow-sm pointer-events-none select-none"
      role="status"
      aria-live="polite"
      aria-label={`Grid spacing ${label}`}
    >
      <GridGlyph />
      <span className="tabular-nums">Grid {label}</span>
    </div>
  );
}

/** Two-by-two grid glyph rendered as a 12px SVG so it sits on the text
 *  baseline. Stroke colors match the canvas grid's coarse + major tints so
 *  the legend reads as "this is what you're seeing on the canvas". */
function GridGlyph() {
  return (
    <svg
      width="12"
      height="12"
      viewBox="0 0 12 12"
      aria-hidden="true"
      className="shrink-0"
    >
      <path
        d="M0 4 H12 M0 8 H12 M4 0 V12 M8 0 V12"
        stroke="#bcc0c8"
        strokeWidth={1}
      />
    </svg>
  );
}
